import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { permissionsApi, Permission, CreatePermissionRequest, UpdatePermissionRequest, GetPermissionsParams } from '../services/permissionsApi'

interface PermissionsState {
  permissions: Permission[]
  pagination: {
    total: number
    page: number
    limit: number
  }
  loading: boolean
  error: string | null
}

interface PermissionsActions {
  fetchPermissions: (params?: GetPermissionsParams) => Promise<void>
  createPermission: (permissionData: CreatePermissionRequest) => Promise<void>
  updatePermission: (permissionId: string, permissionData: UpdatePermissionRequest) => Promise<void>
  deletePermission: (permissionId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

type PermissionsStore = PermissionsState & PermissionsActions

export const usePermissionsStore = create<PermissionsStore>()(
  persist(
    (set, get) => ({
      // Initial state
      permissions: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 50
      },
      loading: false,
      error: null,

      // Actions
      fetchPermissions: async (params?: GetPermissionsParams) => {
        set({ loading: true, error: null })
        
        try {
          const response = await permissionsApi.getPermissions(params)
          
          if (response.success && response.data) {
            set({
              permissions: response.data.permissions || [],
              pagination: {
                total: response.data.total || 0,
                page: response.data.page || 1,
                limit: response.data.limit || 50
              },
              loading: false,
              error: null,
            })
          } else {
            set({
              permissions: [],
              pagination: { total: 0, page: 1, limit: 50 },
              loading: false,
              error: 'Failed to fetch permissions',
            })
          }
        } catch (error: any) {
          set({
            permissions: [],
            pagination: { total: 0, page: 1, limit: 50 },
            loading: false,
            error: error.message || 'Failed to fetch permissions',
          })
        }
      },

      createPermission: async (permissionData: CreatePermissionRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await permissionsApi.createPermission(permissionData)
          
          if (response.success && response.data) {
            const { permissions } = get()
            set({
              permissions: [...permissions, response.data],
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to create permission',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create permission',
          })
        }
      },

      updatePermission: async (permissionId: string, permissionData: UpdatePermissionRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await permissionsApi.updatePermission(permissionId, permissionData)
          
          if (response.success && response.data) {
            const { permissions } = get()
            set({
              permissions: permissions.map(permission => 
                permission._id === permissionId ? response.data! : permission
              ),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to update permission',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to update permission',
          })
        }
      },

      deletePermission: async (permissionId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await permissionsApi.deletePermission(permissionId)
          
          if (response.success) {
            const { permissions } = get()
            set({
              permissions: permissions.filter(permission => permission._id !== permissionId),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to delete permission',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete permission',
          })
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'permissions-storage',
      partialize: (state) => ({
        permissions: state.permissions,
      }),
    }
  )
)
